import { darken } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { ReportWidgetChartTooltip } from './ReportWidgetChartTooltip';
import { ReportWidgetXAxisWrappedTick } from './ReportWidgetXAxisWrappedTick';

import {
    buildBarChartWrappedTickPreview,
    resolveBarChartXAxisHeight,
    shouldRotateBarChartTicks,
} from '../../model/lib/barChartHelpers';
import {
    REPORT_CHART_AXIS_STROKE,
    REPORT_CHART_GRID_STROKE_OPACITY,
} from '../chartColors';

import type {
    ReportWidgetChartPoint,
    ReportWidgetItem,
    ReportWidgetTooltipRowsBuilder,
} from '../../model/types';

interface ReportWidgetBarChartProps {
    data: ReportWidgetChartPoint[];
    showLegend: boolean;
    items: ReportWidgetItem[];
    total: number;
    tooltipFormatter: (value: number) => string;
    tooltipRowsBuilder?: ReportWidgetTooltipRowsBuilder;
    onItemClick?: (item: ReportWidgetItem) => void;
    width?: number;
    height?: number;
    activeItemId?: string;
    disableAnimation?: boolean;
    isPrinting?: boolean;
    onItemHover?: (item: ReportWidgetItem | null) => void;
    yAxisTickFormatter?: (value: number) => string;
    xAxisTickFormatter?: (
        label: string | number,
        index: number,
        data: ReportWidgetChartPoint[],
    ) => string;
    xAxisTickWrap?: {
        maxLines?: number;
        maxCharsPerLine?: number;
    };
    /** When set, overrides automatic rotation heuristic (line charts use the same option). */
    xAxisTickAngle?: number;
}

export const ReportWidgetBarChart = ({
    data,
    showLegend,
    tooltipFormatter,
    tooltipRowsBuilder,
    items,
    total,
    onItemClick,
    width,
    height,
    activeItemId,
    disableAnimation = false,
    isPrinting = false,
    onItemHover,
    yAxisTickFormatter,
    xAxisTickFormatter,
    xAxisTickWrap,
    xAxisTickAngle,
}: ReportWidgetBarChartProps) => {
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const chartHeight =
        isPrinting && typeof height === 'number' ? height - 20 : height;

    // So depending on the x axis label length, we may need to rotate the ticks OR wrap them.
    // This also drives the height of the x axis.
    const shouldRotateTicks = shouldRotateBarChartTicks(data, xAxisTickAngle);
    const autoTickAngle = shouldRotateTicks ? -20 : 0;
    const resolvedTickAngle = xAxisTickAngle ?? autoTickAngle;

    const shouldWrapTicks = Boolean(xAxisTickWrap);

    const precomputedWrappedTicks = useMemo(
        () =>
            buildBarChartWrappedTickPreview({
                data,
                shouldWrapTicks,
                xAxisTickFormatter,
                xAxisTickWrap,
            }),
        [data, shouldWrapTicks, xAxisTickFormatter, xAxisTickWrap],
    );

    const xAxisHeight = resolveBarChartXAxisHeight({
        shouldRotateTicks,
        shouldWrapTicks,
        wrappedTicks: precomputedWrappedTicks,
    });

    return (
        <BarChart data={data} width={width} height={chartHeight}>
            <CartesianGrid
                stroke={REPORT_CHART_AXIS_STROKE}
                strokeWidth={1}
                strokeOpacity={REPORT_CHART_GRID_STROKE_OPACITY}
                vertical={false}
            />
            <XAxis
                dataKey="name"
                tickLine={false}
                interval={0}
                height={xAxisHeight}
                axisLine={{ stroke: REPORT_CHART_AXIS_STROKE, strokeWidth: 1 }}
                tick={
                    shouldWrapTicks ? (
                        <ReportWidgetXAxisWrappedTick
                            data={data}
                            formatter={xAxisTickFormatter}
                            precomputedTicks={precomputedWrappedTicks}
                            angle={resolvedTickAngle}
                            maxLines={xAxisTickWrap?.maxLines ?? 2}
                            maxCharsPerLine={
                                xAxisTickWrap?.maxCharsPerLine ?? 24
                            }
                        />
                    ) : (
                        { fontSize: '1.2rem', fill: '#000000' }
                    )
                }
                minTickGap={shouldRotateTicks ? 8 : 16}
                angle={resolvedTickAngle}
                textAnchor={resolvedTickAngle === 0 ? 'middle' : 'end'}
                tickFormatter={(value: string | number, index: number) =>
                    xAxisTickFormatter
                        ? xAxisTickFormatter(value, index, data)
                        : String(value)
                }
            />
            <YAxis
                tick={{ fontSize: 12, fill: '#000000' }}
                tickLine={{ stroke: REPORT_CHART_AXIS_STROKE, strokeWidth: 1 }}
                tickSize={10}
                tickMargin={6}
                tickFormatter={(v) => {
                    if (yAxisTickFormatter) {
                        return yAxisTickFormatter(v as number);
                    }

                    if (typeof v !== 'number') {
                        return String(v);
                    }

                    if (v === 0) {
                        return '0';
                    }

                    return tooltipFormatter(v);
                }}
                domain={[
                    'auto',
                    (dataMax: number) =>
                        Number.isFinite(dataMax) ? dataMax * 1.2 : dataMax,
                ]}
                axisLine={{ stroke: REPORT_CHART_AXIS_STROKE, strokeWidth: 1 }}
            />
            <Tooltip
                cursor={{ fill: 'transparent' }}
                content={
                    <ReportWidgetChartTooltip
                        tooltipFormatter={tooltipFormatter}
                        tooltipRowsBuilder={tooltipRowsBuilder}
                        total={total}
                        items={items}
                    />
                }
            />
            {showLegend && (
                <Legend
                    verticalAlign="top"
                    align="left"
                    iconType="circle"
                    wrapperStyle={{
                        paddingBottom: '18px',
                        fontSize: '1.2rem',
                        lineHeight: '1.6rem',
                    }}
                />
            )}
            <Bar
                dataKey="value"
                fill="#5ca1ff"
                radius={[4, 4, 0, 0]}
                isAnimationActive={!disableAnimation}
                cursor={onItemClick ? 'pointer' : 'default'}
                maxBarSize={150}
                minPointSize={8}
            >
                {data.map((entry, index) => {
                    const item = items[index];
                    const isHovered = entry.id === hoveredId;
                    const isActive = entry.id === activeItemId;

                    const baseColor = entry.color;
                    const fillColor =
                        isHovered || isActive
                            ? darken(baseColor, 0.2)
                            : baseColor;

                    return (
                        <Cell
                            key={entry.id}
                            fill={fillColor}
                            style={{
                                transition: 'fill 120ms ease-out',
                            }}
                            onClick={() =>
                                onItemClick && item && onItemClick(item)
                            }
                            onMouseEnter={() => {
                                setHoveredId(entry.id);
                                if (onItemHover && item) {
                                    onItemHover(item);
                                }
                            }}
                            onMouseLeave={() => {
                                setHoveredId((current) =>
                                    current === entry.id ? null : current,
                                );
                                if (onItemHover) {
                                    onItemHover(null);
                                }
                            }}
                        />
                    );
                })}
            </Bar>
        </BarChart>
    );
};
